import { ANALYTICS_EVENTS, UPSELL_ITEM_MAX_QUANTITY, UPSELL_ITEM_MAX_QUANTITY_CATERING } from '@nandosaus/constants';
import { find, isEmpty, noop } from 'lodash';
import { observer } from 'mobx-react';
import { useRootStore } from '@nandosaus/state-management';
import {
  ensureProductDetailed,
  ensureProductsDetailed,
  getDefaultChoices,
  productRequiresConfiguration,
} from '@nandosaus/state-management/lib/helpers/product-configuration';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { analytics } from '@analytics';
import { MenuItemCardSkeleton } from '@components/card/menu-item-card-skeleton';
import { Box, Flex } from '../grid';
import { Button } from '../button';
import { Button as NewButton } from '../button/new';
import { P } from '../typography';
import { P as NewP } from '../typography/new';
import { Modal } from '../modal';
import { Modal as NewModal } from '../modal/new';
import { ProductDetailModal } from '../product-detail-modal';
import { UpsellItemCard } from '../card/upsell-item-card';
import { OrderAtTableCarousel } from '../carousel/order-at-table-carousel';
import { UpsellModalHeader } from './upsell-modal-header';
import { useFeature } from '../../hooks/use-feature';
import { logger } from '../../utils/logger';

const ANALYTICS_LOCATION = 'Upsell Modal';

// The flyout design lays the tiles out in a 2x2 grid. The pre-flag carousel shows 3.
const CONFIGURABLE_UPSELL_ITEM_COUNT = 4;
const LEGACY_UPSELL_ITEM_COUNT = 3;

const UpsellModal = ({
  modalOpen,
  handleModalClose,
  recommendedProducts,
  newProductIds,
  content,
  brazeLogImpression,
  brazeLogClick,
}) => {
  const { CartStore, MenuStore, DeliveryStore, ProductDetailsState } = useRootStore();

  // With the flag off this component keeps its local `productQuantities` batching and the
  // legacy centred modal. With it on the panel becomes a right-side flyout that writes
  // straight to the cart — see the comment on addProductToCart.
  const isConfigurableUpsellEnabled = useFeature('configurable-offer-items-enabled');
  const upsellItemCount = isConfigurableUpsellEnabled ? CONFIGURABLE_UPSELL_ITEM_COUNT : LEGACY_UPSELL_ITEM_COUNT;

  const [productQuantities, setProductQuantities] = useState({});
  const [customisingProductId, setCustomisingProductId] = useState(null);
  const [upsellItemsNotInCart, setUpsellItemsNotInCart] = useState([
    recommendedProducts
      .filter(product => {
        const quantityInCart = parseInt(CartStore.productQuantityInCart(product.id), 10);
        return quantityInCart < 1;
      })
      .slice(0, upsellItemCount),
  ]);
  const { heading, disclaimer, cta } = content || {};
  const { hasReordered } = CartStore;

  const { loading, isCateringMenu } = MenuStore;

  const { shortestDeliveryTime } = DeliveryStore;

  const maxQuantity = isCateringMenu ? UPSELL_ITEM_MAX_QUANTITY_CATERING : UPSELL_ITEM_MAX_QUANTITY;

  const closeModal = () => {
    analytics.track(
      hasReordered ? ANALYTICS_EVENTS.UPSELL_MODAL_CLOSED_AFTER_REORDER : ANALYTICS_EVENTS.UPSELL_MODAL_CLOSED
    );
    handleModalClose();
  };

  const addItem = (product, quantity = 1) => {
    const selectedProductQuantity = productQuantities[product.plu] || 0;
    const newQuantity = selectedProductQuantity + quantity;

    if (newQuantity > maxQuantity) {
      return null;
    }

    // Only track analytics on the first add
    if (selectedProductQuantity === 0) {
      brazeLogClick?.(product.plu);

      analytics.track(
        hasReordered ? ANALYTICS_EVENTS.UPSELL_ITEM_ADDED_AFTER_REORDER : ANALYTICS_EVENTS.UPSELL_ITEM_ADDED,
        {
          product_id: product.plu,
          name: product.name,
          category: product.productCategory?.name,
        }
      );
    }

    return setProductQuantities({ ...productQuantities, [product.plu]: newQuantity });
  };

  const removeItem = (product, quantity = 1) => {
    const selectedProductQuantity = productQuantities[product.plu] || 0;
    const newQuantity = selectedProductQuantity - quantity;

    if (newQuantity <= 0) {
      analytics.track(
        hasReordered ? ANALYTICS_EVENTS.UPSELL_ITEM_REMOVED_AFTER_REORDER : ANALYTICS_EVENTS.UPSELL_ITEM_REMOVED,
        {
          product_id: product.plu,
          name: product.name,
          category: product.productCategory?.name,
        }
      );

      const newProductQuantities = { ...productQuantities };
      delete newProductQuantities[product.plu];
      return setProductQuantities(newProductQuantities);
    }

    return setProductQuantities({ ...productQuantities, [product.plu]: newQuantity });
  };

  const trackUpsellItemAdded = product => {
    brazeLogClick?.(product.plu);

    analytics.track(
      hasReordered ? ANALYTICS_EVENTS.UPSELL_ITEM_ADDED_AFTER_REORDER : ANALYTICS_EVENTS.UPSELL_ITEM_ADDED,
      {
        product_id: product.plu,
        name: product.name,
        category: product.productCategory?.name,
      }
    );
  };

  const cartOrderItemForProduct = product =>
    find(CartStore.orderItems, orderItem => orderItem.product?.id === product.id);

  /**
   * DOUBLE-ADD RECONCILIATION.
   *
   * The pre-flag panel keeps its own `productQuantities` tally and only writes to the cart
   * when the tile's "Add" button is pressed. A PDP round-trip, by contrast, writes to the
   * cart immediately via saveToCart — so running both would add a configured item twice
   * (once by the PDP, once by the panel's own tally).
   *
   * Rather than try to keep the two in step, the configurable flow has exactly ONE write
   * path: the cart itself. `productQuantities` is untouched here, every tile reads its
   * quantity from CartStore.productQuantityInCart, and the stepper mutates the cart line
   * directly. Nothing is batched, so nothing can be double-applied.
   */
  const addProductToCart = product => {
    CartStore.addOrderItem({
      product,
      quantity: 1,
      // Safe here precisely because requiresConfiguration === false for this branch —
      // the only default this fills is a mild basting.
      choices: getDefaultChoices(product),
      isUpsellItem: true,
    });

    trackUpsellItemAdded(product);

    analytics.track(ANALYTICS_EVENTS.PROMOTION_CLICKED, { location: ANALYTICS_LOCATION });
  };

  // Incrementing the existing cart line is what makes "increasing quantity reuses the same
  // configuration" true — the line keeps the choices it was created with.
  const incrementProductInCart = product => {
    const orderItem = cartOrderItemForProduct(product);

    if (orderItem) {
      CartStore.incrementOrderItem(orderItem);
    }
  };

  const decrementProductInCart = product => {
    const orderItem = cartOrderItemForProduct(product);

    if (!orderItem) {
      return;
    }

    if (orderItem.quantity > 1) {
      CartStore.decrementOrderItem(orderItem);
      return;
    }

    analytics.track(
      hasReordered ? ANALYTICS_EVENTS.UPSELL_ITEM_REMOVED_AFTER_REORDER : ANALYTICS_EVENTS.UPSELL_ITEM_REMOVED,
      {
        product_id: product.plu,
        name: product.name,
        category: product.productCategory?.name,
      }
    );

    CartStore.removeOrderItem(orderItem);
  };

  const isOpeningCustomisation = useRef(false);

  /**
   * Open the PDP for an upsell item so its required choices can be answered.
   *
   * The PDP renders as a CHILD of this panel's modal, so the panel stays mounted behind it
   * and exiting without adding returns the customer here with nothing added.
   *
   * ensureProductDetailed must run first — a meal product's item sibling comes from
   * `otherSizes`, which read as undefined until loaded, and the PDP derives displayProduct
   * from them. The originating-surface state (`customisingProductId`) is deliberately kept
   * in this component rather than in ProductDetailsState, because the PDP resets
   * ProductDetailsState on unmount.
   */
  const customiseProduct = async product => {
    if (isOpeningCustomisation.current) {
      return;
    }

    isOpeningCustomisation.current = true;

    try {
      await ensureProductDetailed({ menu: MenuStore.menu, product, logger });
      ProductDetailsState.viewProduct({ product });
      setCustomisingProductId(product.id);

      analytics.track(ANALYTICS_EVENTS.PROMOTION_CLICKED, { location: ANALYTICS_LOCATION });
    } finally {
      isOpeningCustomisation.current = false;
    }
  };

  const addSelectedItemsToCart = product => {
    const selectedQuantity = productQuantities[product.plu] || 0;

    const hasSelectedProduct = !isEmpty(productQuantities);
    analytics.track(hasSelectedProduct ? ANALYTICS_EVENTS.PROMOTION_APPLIED : ANALYTICS_EVENTS.UPSELL_MODAL_CLOSED, {
      location: ANALYTICS_LOCATION,
    });

    CartStore.addOrderItem({ product, quantity: selectedQuantity, isUpsellItem: true });
  };

  useEffect(() => {
    if (!modalOpen) {
      return;
    }

    analytics.track(ANALYTICS_EVENTS.PROMOTION_VIEWED, {
      location: ANALYTICS_LOCATION,
    });

    analytics.track(ANALYTICS_EVENTS.PRODUCT_LIST_VIEWED, {
      category: ANALYTICS_LOCATION,
      products: recommendedProducts.map((product, index) => {
        return {
          product_id: product.plu,
          name: product.name,
          position: index,
        };
      }),
    });
  }, [modalOpen, heading, recommendedProducts]);

  useEffect(() => {
    if (!modalOpen) {
      return;
    }

    brazeLogImpression?.();
  }, [modalOpen, brazeLogImpression]);

  /**
   * Resolve details for the displayed items as soon as the panel opens.
   *
   * productRequiresConfiguration can only answer null for an undetailed product, and null
   * has to be treated as "needs configuring". Braze-sourced upsell products are not
   * detailed, so without this every tile would show the customise control and a plain side
   * would lose its one-tap add. Failures are swallowed by the helper, which just leaves the
   * tiles on the safe control.
   */
  useEffect(() => {
    if (!isConfigurableUpsellEnabled || !modalOpen || isEmpty(upsellItemsNotInCart)) {
      return;
    }

    ensureProductsDetailed({ menu: MenuStore.menu, products: upsellItemsNotInCart, logger });
  }, [isConfigurableUpsellEnabled, modalOpen, upsellItemsNotInCart, MenuStore.menu]);

  const hasFrozenUpsellItems = useRef(false);
  useEffect(() => {
    // Under the flag the displayed set is chosen once and then held: a tile has to survive
    // being added so it can show its quantity badge and stepper. Re-filtering on cart state
    // would make the tile vanish the moment it was added.
    if (isConfigurableUpsellEnabled && hasFrozenUpsellItems.current) {
      return;
    }

    const recommendedProductsNotInCart = recommendedProducts
      .filter(product => {
        const quantityInCart = parseInt(CartStore.productQuantityInCart(product.id), 10);
        return quantityInCart < 1;
      })
      .slice(0, upsellItemCount);

    if (isConfigurableUpsellEnabled && recommendedProductsNotInCart.length > 0) {
      hasFrozenUpsellItems.current = true;
    }

    setUpsellItemsNotInCart(recommendedProductsNotInCart);
  }, [CartStore, recommendedProducts, isConfigurableUpsellEnabled, upsellItemCount]);

  if (!upsellItemsNotInCart?.length) {
    return null;
  }

  if (isConfigurableUpsellEnabled) {
    return (
      <NewModal
        open={modalOpen}
        handleClose={closeModal}
        modalTitle=""
        headerBackgroundColor="transparent"
        headerButtonColor="black"
        headerButtonWithCircularBackground
        hideBottomDecal
        showShadow
        // The flyout in the design sits flush to the right edge at 520px on desktop and
        // becomes a full-width bottom sheet below md — which is exactly what variant="side"
        // already does, so no bespoke width/height is needed here.
        variant="side"
        footer={
          // Only the CTA is sticky — the disclaimer scrolls with the tiles (in the body below)
          // so it can't sit on top of them.
          // widthType="full" (not "fullWidth") — only 'full' | 'fit' | 'fixed' are mapped to a
          // width, despite the internal style key being called fullWidth.
          <NewButton size="tall" widthType="full" onClick={handleModalClose} data-testid="continue-button">
            {cta}
          </NewButton>
        }
      >
        <Box>
          <UpsellModalHeader heading={heading} variant="flyout" />
          {loading ? (
            <Flex flexWrap="wrap" gap="24px" mt="24px">
              <MenuItemCardSkeleton role="menuitem" />
              <MenuItemCardSkeleton role="menuitem" />
            </Flex>
          ) : (
            <Box mt="24px" display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gridGap="24px" role="list">
              {upsellItemsNotInCart.map(product => (
                <Box key={product.plu} role="listitem">
                  <UpsellItemCard
                    isConfigurableFlowEnabled
                    requiresConfiguration={productRequiresConfiguration(product)}
                    item={{
                      ...product,
                      categoryName: product.productCategory?.name,
                      formattedDietaryPreferencesList: product.formattedDietaryPreferencesList,
                      formattedKilojoules: product.formattedKilojoules,
                      hasOtherSizes: product.hasOtherSizes,
                      hasVariablePrice: product.hasVariablePrice,
                      isAvailable: product.isAnySizeAvailable,
                      imageAlt: product.name,
                      isEligibleForDeliveryDiscount: product.isEligibleForDeliveryDiscount,
                      plu: product.plu,
                      key: product.plu,
                    }}
                    isNew={newProductIds.includes(product.plu)}
                    isItemInCart={parseInt(CartStore.productQuantityInCart(product.id), 10) > 0}
                    isDiscountApplied={shortestDeliveryTime?.isDiscountApplied}
                    onAdd={() => addProductToCart(product)}
                    onCustomise={() => customiseProduct(product)}
                    onIncrement={() => incrementProductInCart(product)}
                    onDecrement={() => decrementProductInCart(product)}
                    // Unused on this path — the cart is the single source of quantity.
                    addItem={noop}
                    removeItem={noop}
                    addSelectedItemsToCart={noop}
                  />
                </Box>
              ))}
            </Box>
          )}
          <Box mt="24px" textAlign="center">
            <NewP variant="standard">{disclaimer}</NewP>
          </Box>
        </Box>

        {customisingProductId && (
          <ProductDetailModal
            modalOpen
            saveToCartAttribution={{ isUpsellItem: true }}
            // Both close paths return here with the panel still mounted behind. Closing
            // without adding leaves the cart untouched.
            handleModalClose={() => setCustomisingProductId(null)}
            handleModalSubmit={() => setCustomisingProductId(null)}
          />
        )}
      </NewModal>
    );
  }

  return (
    <Modal
      modalTitle=""
      handleClose={closeModal}
      open={modalOpen}
      isDividerHidden
      headerBackgroundColor="transparent"
      headerButtonColor="black"
      footerBackgroundColor="transparent"
      width={{
        _: '100%',
        md: '680px',
      }}
      contentButtons={
        <Flex justifyContent="center" width="100%">
          <Button
            variant="primary"
            onClick={handleModalClose}
            mx={{ _: '24px', md: '40px' }}
            data-testid="continue-button"
            width={{ xs: '100%', lg: 'fit-content' }}
          >
            {cta}
          </Button>
        </Flex>
      }
      buttonContainerStyle={{ position: 'relative', mt: 1 }}
      childrenContainerStyle={{ marginBottom: 0 }}
      wrapperStyle={{ justifyContent: 'flex-start' }}
    >
      <Box position="relative">
        <UpsellModalHeader heading={heading} />
        <Flex flexDirection="row">
          {loading ? (
            <OrderAtTableCarousel>
              <MenuItemCardSkeleton role="menuitem" />
              <MenuItemCardSkeleton role="menuitem" />
              <MenuItemCardSkeleton role="menuitem" />
            </OrderAtTableCarousel>
          ) : (
            <OrderAtTableCarousel px={{ _: '24px', md: '40px' }}>
              {upsellItemsNotInCart.map(product => {
                const selectedProductQuantity = productQuantities[product.plu];
                const isNew = newProductIds.includes(product.plu);

                const quantityInCart = parseInt(CartStore.productQuantityInCart(product.id), 10);
                const isItemInCart = quantityInCart > 0;
                const isDiscountApplied = shortestDeliveryTime?.isDiscountApplied;
                return (
                  <UpsellItemCard
                    key={product.plu}
                    item={{
                      ...product,
                      categoryName: product.productCategory?.name,
                      formattedDietaryPreferencesList: product.formattedDietaryPreferencesList,
                      formattedKilojoules: product.formattedKilojoules,
                      hasOtherSizes: product.hasOtherSizes,
                      hasVariablePrice: product.hasVariablePrice,
                      isAvailable: product.isAnySizeAvailable,
                      imageAlt: product.name,
                      isEligibleForDeliveryDiscount: product.isEligibleForDeliveryDiscount,
                      plu: product.plu,
                      key: product.plu,
                    }}
                    addSelectedItemsToCart={() => {
                      addSelectedItemsToCart(product);
                    }}
                    addItem={(quantity = 1) => {
                      addItem(product, quantity);

                      analytics.track(ANALYTICS_EVENTS.PROMOTION_CLICKED, {
                        location: ANALYTICS_LOCATION,
                      });
                    }}
                    removeItem={(quantity = 1) => removeItem(product, quantity)}
                    isNew={isNew}
                    isItemInCart={isItemInCart}
                    isDiscountApplied={isDiscountApplied}
                    quantitySelected={selectedProductQuantity ?? 0}
                  />
                );
              })}
            </OrderAtTableCarousel>
          )}
        </Flex>
        <Box textAlign="center" zIndex="10" position="relative" mt={1} mx={{ _: '24px', md: '40px' }}>
          <P variant={2} hideTramlines>
            {`${disclaimer} `}
          </P>
        </Box>
      </Box>
    </Modal>
  );
};

UpsellModal.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  handleModalClose: PropTypes.func.isRequired,
  recommendedProducts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      plu: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      prices: PropTypes.shape({
        formattedPrice: PropTypes.string.isRequired,
      }).isRequired,
      image: PropTypes.string.isRequired,
    })
  ).isRequired,
  newProductIds: PropTypes.arrayOf(PropTypes.string),
  content: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    subheading: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    disclaimer: PropTypes.string.isRequired,
    cta: PropTypes.string.isRequired,
  }).isRequired,
  brazeLogImpression: PropTypes.func,
  brazeLogClick: PropTypes.func,
};

UpsellModal.defaultProps = {
  newProductIds: [],
  brazeLogImpression: null,
  brazeLogClick: null,
};

const ConnectedUpsellModal = observer(UpsellModal);

export { ConnectedUpsellModal as UpsellModal };
